import { formatGr, type Id } from '@odhaczka/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { useState } from 'react';
import { Link } from 'react-router';
import { BottomNav, SyncBadge } from '../components/Layout';
import { PriceSearch } from '../components/prices/PriceSearch';
import { PromosSection } from '../components/prices/PromosSection';
import { SpendingSection } from '../components/prices/SpendingSection';
import { plural } from '../components/shop/status';
import { cardCls, inputCls, secondaryBtn, sectionTitleCls } from '../components/ui/classes';
import { PageHeader } from '../components/ui/PageHeader';
import { SkeletonRows } from '../components/ui/Skeleton';
import { useReceipts } from '../data/receipts';
import { monthKey, monthLabel } from '../data/spending';
import { db } from '../db/db';
import type { ReceiptRow } from '../db/types';

const MONTHS_SHOWN = 6;


/** Zakładka Ceny: wyszukiwanie cen, wydatki miesięcy i paragony z Lidla. */
export function PricesPage() {
  const receipts = useReceipts();
  const lists = useLiveQuery(async () => new Map((await db.lists.toArray()).map((l) => [l.id, l.title])));
  // Linie czekające na potwierdzenie, po paragonie.
  const toConfirm = useLiveQuery(async () => {
    const m = new Map<Id, number>();
    for (const l of await db.receiptLines.filter((x) => x.match === 'suggested' && !x.deleted).toArray()) m.set(l.receiptId, (m.get(l.receiptId) ?? 0) + 1);
    return m;
  });
  const [months, setMonths] = useState(MONTHS_SHOWN);
  const [query, setQuery] = useState('');

  const groups = new Map<string, ReceiptRow[]>();
  for (const r of receipts ?? []) {
    if (!r.purchasedMs) continue;
    const k = monthKey(r.purchasedMs);
    groups.set(k, [...(groups.get(k) ?? []), r]);
  }
  const keys = [...groups.keys()];

  return (
    <>
      <PageHeader title="Ceny" right={<SyncBadge />} />
      <main className="flex flex-1 flex-col gap-5 p-4">
        {receipts === undefined ? (
          <SkeletonRows />
        ) : receipts.length === 0 ? (
          <div className={`${cardCls} flex flex-col gap-3 p-4`}>
            <p className="font-semibold">Nie ma jeszcze paragonów</p>
            <p className="text-sm text-stone-600 dark:text-stone-400">
              Połącz Lidl Plus w zakładce Dom. Odhaczka pobierze e-paragony, dopasuje je do list i pokaże ceny przy pozycjach.
            </p>
            <Link to="/home#lidl" className={secondaryBtn}>
              Połącz Lidl Plus
            </Link>
          </div>
        ) : (
          <>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Szukaj ceny, np. masło"
              enterKeyHint="search"
              className={inputCls}
            />
            {query.trim() ? (
              <PriceSearch query={query} />
            ) : (
              <>
                <PromosSection />
                <SpendingSection receipts={receipts} />
              </>
            )}
            {!query.trim() && <h2 className={`${sectionTitleCls} -mb-3`}>Paragony</h2>}
            {!query.trim() && keys.slice(0, months).map((k) => {
              const rs = groups.get(k)!;
              const sum = rs.reduce((s, r) => s + (r.totalGr ?? 0), 0);
              return (
                <section key={k} className="flex flex-col gap-2">
                  <h2 className={`${sectionTitleCls} flex items-baseline gap-2`}>
                    <span className="flex-1">{monthLabel(k)}</span>
                    <span className="tabular-nums normal-case">
                      {rs.length} {plural(rs.length, 'paragon', 'paragony', 'paragonów')} · {formatGr(sum)}
                    </span>
                  </h2>
                  <ul className={`${cardCls} divide-y divide-stone-200 dark:divide-stone-800`}>
                    {rs.map((r) => (
                      <ReceiptRowView key={r.id} receipt={r} listTitle={r.listId ? (lists?.get(r.listId) ?? null) : null} toConfirm={toConfirm?.get(r.id) ?? 0} />
                    ))}
                  </ul>
                </section>
              );
            })}
            {!query.trim() && keys.length > months && (
              <button type="button" onClick={() => setMonths((m) => m + 12)} className={secondaryBtn}>
                Pokaż starsze
              </button>
            )}
          </>
        )}
      </main>
      <BottomNav />
    </>
  );
}

function ReceiptRowView({ receipt, listTitle, toConfirm }: { receipt: ReceiptRow; listTitle: string | null; toConfirm: number }) {
  const when = receipt.purchasedMs
    ? new Date(receipt.purchasedMs).toLocaleString('pl-PL', { weekday: 'short', day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '';
  return (
    <li>
      <Link to={`/receipts/${encodeURIComponent(receipt.id)}`} className="flex items-center gap-3 px-4 py-3 active:bg-stone-50 dark:active:bg-stone-800">
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium">{listTitle ?? receipt.storeName ?? 'Lidl'}</div>
          <div className="truncate text-sm text-stone-500">
            {when}
            {listTitle && receipt.storeName ? ` · ${receipt.storeName}` : ''}
          </div>
          {(toConfirm > 0 || receipt.listState === 'ambiguous') && (
            <span className="mt-1 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
              {receipt.listState === 'ambiguous' ? 'wybierz listę' : `${toConfirm} do potwierdzenia`}
            </span>
          )}
        </div>
        <div className="shrink-0 text-right">
          <div className="font-semibold tabular-nums">{formatGr(receipt.totalGr ?? 0)}</div>
          {receipt.savingsGr ? <div className="text-xs text-brand-700 tabular-nums dark:text-brand-500">−{formatGr(receipt.savingsGr)}</div> : null}
        </div>
      </Link>
    </li>
  );
}
